/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — Association UI (Track J.3a — 2026-09-14)
 * ----------------------------------------------------------------------------
 * Archetype : "association" (drag-pairs : lier items gauche → droite)
 * ========================================================================== */

import React, { useState, useRef } from 'react';
import type { BanqueItemAssociation } from '../../../../lib/banque-types';

export interface AssociationProps {
  item: BanqueItemAssociation;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  disabled?: boolean;
  showFeedback?: 'idle' | 'correct' | 'incorrect';
}

export function Association({
  item,
  onAnswer,
  disabled = false,
  showFeedback = 'idle',
}: AssociationProps): JSX.Element {
  // mapping: droite.id -> gauche.id (one-to-one)
  const [pairs, setPairs] = useState<Record<string, string | null>>(
    Object.fromEntries(item.pairesDroite.map((d) => [d.id, null])),
  );
  const [submitted, setSubmitted] = useState<boolean>(false);
  const draggedFrom = useRef<{ side: 'gauche'; id: string } | null>(null);

  const gaucheById = new Map(item.pairesGauche.map((g) => [g.id, g]));
  const droiteById = new Map(item.pairesDroite.map((d) => [d.id, d]));

  const handleDragStartGauche = (id: string) => (e: React.DragEvent<HTMLButtonElement>): void => {
    draggedFrom.current = { side: 'gauche', id };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOverDropZone = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropDroite = (droiteId: string) => (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (!draggedFrom.current || draggedFrom.current.side !== 'gauche') return;
    if (submitted || disabled) return;
    const gaucheId = draggedFrom.current.id;
    setPairs((prev) => {
      // If gaucheId was already in another droite, remove it from there
      const next: Record<string, string | null> = { ...prev };
      for (const dId of Object.keys(next)) {
        if (next[dId] === gaucheId) next[dId] = null;
      }
      next[droiteId] = gaucheId;
      return next;
    });
    draggedFrom.current = null;
  };

  const handleClear = (droiteId: string) => (): void => {
    if (submitted || disabled) return;
    setPairs((prev) => ({ ...prev, [droiteId]: null }));
  };

  const handleSubmit = (): void => {
    if (submitted || disabled) return;
    setSubmitted(true);
    let ok = true;
    for (const dId of Object.keys(pairs)) {
      const expected = item.pairesCorrectes[dId];
      if (pairs[dId] !== expected) {
        ok = false;
        break;
      }
    }
    onAnswer(ok, { pairs });
  };

  const allFilled = Object.values(pairs).every((v) => v !== null);
  const correctCount = Object.keys(pairs).filter((dId) => pairs[dId] === item.pairesCorrectes[dId]).length;

  const feedback = submitted
    ? showFeedback !== 'idle'
      ? showFeedback
      : correctCount === Object.keys(pairs).length
      ? 'correct'
      : 'incorrect'
    : 'idle';

  return (
    <section
      className="association"
      data-testid="association"
      data-archetype="association"
      data-item-id={item.id}
      data-feedback={feedback}
    >
      <h3 className="association-consigne">{item.enonce}</h3>
      {item.consigne && <p className="association-meta">{item.consigne}</p>}

      <div className="association-grid">
        <ul className="association-list association-list-gauche" aria-label="Items à associer">
          {item.pairesGauche.map((g) => {
            const used = Object.values(pairs).includes(g.id);
            return (
              <li key={g.id} className="association-item-gauche-wrap">
                <button
                  type="button"
                  className="association-item association-item-gauche"
                  data-testid={`gauche-${g.id}`}
                  data-used={used ? 'true' : 'false'}
                  draggable={!submitted && !disabled}
                  aria-grabbed="false"
                  aria-label={`${g.label} (glisser vers la droite)`}
                  onDragStart={handleDragStartGauche(g.id)}
                  disabled={submitted || disabled}
                >
                  {g.label}
                </button>
              </li>
            );
          })}
        </ul>

        <ul className="association-list association-list-droite" aria-label="Cibles">
          {item.pairesDroite.map((d) => {
            const assignedGaucheId = pairs[d.id];
            const assignedLabel = assignedGaucheId ? gaucheById.get(assignedGaucheId)?.label : null;
            const expectedGaucheId = item.pairesCorrectes[d.id];
            const expectedLabel = expectedGaucheId ? gaucheById.get(expectedGaucheId)?.label : null;
            const isCorrectCell = submitted && assignedGaucheId === expectedGaucheId;
            const isIncorrectCell = submitted && assignedGaucheId !== expectedGaucheId;
            return (
              <li key={d.id} className="association-droite-wrap">
                <div className="association-target-label">{d.label}</div>
                <div
                  className="association-dropzone"
                  data-testid={`dropzone-${d.id}`}
                  data-state={submitted ? (isCorrectCell ? 'correct' : isIncorrectCell ? 'incorrect' : 'empty') : assignedGaucheId ? 'filled' : 'empty'}
                  onDragOver={handleDragOverDropZone}
                  onDrop={handleDropDroite(d.id)}
                  aria-label={`Cible ${d.label}${assignedLabel ? ` (associé à ${assignedLabel})` : ' (vide)'}`}
                >
                  {assignedLabel ? (
                    <>
                      <span className="association-chip" data-testid={`chip-${d.id}`}>{assignedLabel}</span>
                      {!submitted && !disabled && (
                        <button
                          type="button"
                          className="association-clear"
                          data-testid={`clear-${d.id}`}
                          aria-label={`Retirer ${assignedLabel}`}
                          onClick={handleClear(d.id)}
                        >
                          ×
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="association-dropzone-empty" aria-hidden="true">
                      <span className="association-dropzone-icon">⬇</span>
                    </div>
                  )}
                </div>
                {submitted && !isCorrectCell && expectedLabel && (
                  <div className="association-expected" data-testid={`expected-${d.id}`}>
                    Réponse : {expectedLabel}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <button
        type="button"
        className="association-submit"
        data-action="soumettre"
        data-testid="association-submit"
        onClick={handleSubmit}
        disabled={!allFilled || submitted || disabled}
      >
        Valider
      </button>
      {submitted && (
        <p className="association-feedback" data-testid="association-feedback">
          {correctCount} / {Object.keys(pairs).length} bonnes associations.
        </p>
      )}
    </section>
  );
}

export default Association;